/**
 * Trading-day bucketing in the instrument's EXCHANGE-SESSION timezone — NOT
 * UTC and NOT the user's display timezone. A "session day" is an objective
 * property of the exchange (US equities → Eastern Time), so every viewer
 * buckets bars into the same days regardless of how their axis is labelled.
 *
 * Shared by session-scoped indicators (Session Volume Profile; Pivot Points
 * uses its own copy). The timezone is passed via an indicator's `extendData`
 * and defaults to the US-equities session.
 */

import { regularCloseMinutesFor, sessionClock } from './sessionFilter'

/** US-equities exchange session — the default trading-day boundary. */
export const DEFAULT_SESSION_TZ = 'America/New_York'

/**
 * Where a trading day BEGINS.
 *
 *  - `calendar-day` — midnight in the session timezone. A day is
 *    pre-market → regular → after-hours, all sharing one date.
 *  - `prior-after-hours` — the previous day's close. A day becomes
 *    yesterday's after-hours → today's pre-market → today's regular session,
 *    so the whole run is attributed to the day it actually drives.
 *
 * The second exists for small caps, where a move is routinely set off by news
 * released AFTER the close: bucketing by calendar day splits that volume from
 * the session it caused, leaving a profile whose levels describe two different
 * stories. Shifting the boundary to the close keeps the reaction with its cause.
 */
export type SessionAnchor = 'calendar-day' | 'prior-after-hours'

/** Narrow an untyped calc param to a SessionAnchor (default `calendar-day`). */
export function toSessionAnchor(value: unknown): SessionAnchor {
  return String(value ?? 'calendar-day') === 'prior-after-hours'
    ? 'prior-after-hours'
    : 'calendar-day'
}

/** Session timezone from an indicator's untyped `extendData`, defaulted. */
export function sessionTimezoneOf(extendData: unknown): string {
  return (extendData as { timezone?: string } | undefined)?.timezone ?? DEFAULT_SESSION_TZ
}

/** Advance a 'YYYY-MM-DD' key by one calendar day. */
function nextDayKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number)
  // UTC arithmetic on a date-ONLY value: no timezone is involved at this point,
  // the key has already been resolved in the session timezone. Using UTC avoids
  // the host timezone shifting the result across a boundary.
  const next = new Date(Date.UTC(y, m - 1, d) + 86_400_000)
  const mm = String(next.getUTCMonth() + 1).padStart(2, '0')
  const dd = String(next.getUTCDate()).padStart(2, '0')
  return `${next.getUTCFullYear()}-${mm}-${dd}`
}

/**
 * Stable 'YYYY-MM-DD' key for a timestamp in the given session timezone.
 *
 * Built on `sessionClock`, whose per-timezone timestamp cache makes repeat
 * lookups a Map hit — draw paths call this once per bar per frame, so an
 * uncached Intl format here dominated whole-frame cost on long datasets.
 *
 * For `prior-after-hours`, bars at or after the regular close belong to the
 * NEXT trading day, so after-hours leads the session it precedes rather than
 * trailing the one it followed. The boundary comes from
 * `regularCloseMinutesFor`, so it moves to 13:00 on NYSE early-close days.
 */
export function sessionDayKey(
  timestamp: number,
  tz: string = DEFAULT_SESSION_TZ,
  anchor: SessionAnchor = 'calendar-day',
): string {
  const { minutes, dateStr } = sessionClock(timestamp, tz)
  if (anchor === 'prior-after-hours' && minutes >= regularCloseMinutesFor(dateStr)) {
    return nextDayKey(dateStr)
  }
  return dateStr
}

export interface SessionRun {
  /** First bar index of the run (inclusive). */
  startIdx: number
  /** Last bar index of the run (inclusive). */
  endIdx: number
}

/**
 * Contiguous runs of bar indices sharing a session day, over `[from, to)`.
 * Bars are assumed ascending by timestamp (as klinecharts stores them), so a
 * day is one unbroken run. Returns runs in chart order.
 */
export function sessionRuns(
  timestamps: number[],
  from: number,
  to: number,
  tz: string = DEFAULT_SESSION_TZ,
  anchor: SessionAnchor = 'calendar-day',
): SessionRun[] {
  const runs: SessionRun[] = []
  let curKey: string | null = null
  let startIdx = -1
  for (let i = from; i < to; i++) {
    const ts = timestamps[i]
    if (ts == null) continue
    const key = sessionDayKey(ts, tz, anchor)
    if (key !== curKey) {
      if (curKey !== null) runs.push({ startIdx, endIdx: i - 1 })
      curKey = key
      startIdx = i
    }
  }
  if (curKey !== null && startIdx >= 0) runs.push({ startIdx, endIdx: to - 1 })
  return runs
}

/**
 * First bar index of the `days` session days ending at `endExclusive`.
 *
 * Walks BACKWARD from the last bar before `endExclusive`, counting distinct
 * session-day keys, and returns the index where the oldest included day begins —
 * so the slice `[result, endExclusive)` is exactly `days` sessions. The final day
 * counts as one even when still in progress, which is the behaviour a day-2 trader
 * expects: "2 days" means today plus yesterday, not "the last two COMPLETED days".
 *
 * `endExclusive` defaults to the whole array. Passing the viewport's right edge
 * instead is what lets a profile follow where you have scrolled rather than always
 * describing the newest data — the same count of days, measured from a different
 * point.
 *
 * Returns 0 when fewer than `days` days are available (use everything) and
 * `endExclusive` when there is nothing to profile, so the caller's slice comes out
 * empty rather than negative.
 */
export function lastSessionDaysStart(
  timestamps: number[],
  days: number,
  tz: string = DEFAULT_SESSION_TZ,
  anchor: SessionAnchor = 'calendar-day',
  endExclusive: number = timestamps.length,
): number {
  const end = Math.min(Math.max(endExclusive, 0), timestamps.length)
  if (end === 0) return 0
  if (days < 1) return end
  let seen = 0
  let curKey: string | null = null
  for (let i = end - 1; i >= 0; i--) {
    const ts = timestamps[i]
    if (ts == null) continue
    const key = sessionDayKey(ts, tz, anchor)
    if (key === curKey) continue
    // A new day begins at i. If the quota is already full, this bar belongs to
    // the day AFTER the window, so the window starts one bar later.
    if (seen === days) return i + 1
    curKey = key
    seen++
  }
  return 0
}

/**
 * Grow a run (from `sessionRuns` over a visible window) to cover its ENTIRE
 * session day in the full `timestamps` array. A run clipped by the viewport —
 * the first/last visible day — extends to the session's real first and last
 * bars, so a session profile is computed from the whole day regardless of how
 * much of it is on screen. Fully-visible interior days are unchanged (their
 * neighbors are different days). The walk is bounded by one day's bar count.
 */
export function expandToFullDay(
  timestamps: number[],
  run: SessionRun,
  tz: string = DEFAULT_SESSION_TZ,
  anchor: SessionAnchor = 'calendar-day',
): SessionRun {
  const key = sessionDayKey(timestamps[run.startIdx], tz, anchor)
  let startIdx = run.startIdx
  while (startIdx - 1 >= 0 && sessionDayKey(timestamps[startIdx - 1], tz, anchor) === key)
    startIdx--
  let endIdx = run.endIdx
  while (
    endIdx + 1 < timestamps.length &&
    sessionDayKey(timestamps[endIdx + 1], tz, anchor) === key
  ) {
    endIdx++
  }
  return { startIdx, endIdx }
}

/**
 * The visible session runs, each grown to its full trading day — the standard
 * preamble of every per-session profile draw (session volume profile, TPO).
 */
export function visibleSessionRuns(
  timestamps: number[],
  from: number,
  to: number,
  tz: string = DEFAULT_SESSION_TZ,
  anchor: SessionAnchor = 'calendar-day',
): SessionRun[] {
  return sessionRuns(timestamps, from, to, tz, anchor).map((r) =>
    expandToFullDay(timestamps, r, tz, anchor),
  )
}

// Keyed by the bar array itself. klinecharts hands `draw` its internal dataList,
// so the reference is stable across frames while the contents only change on a
// tick (append / in-place last-bar update) or a history prepend — both of which
// the length/first/last check catches.
const timestampsCache = new WeakMap<
  readonly { timestamp: number }[],
  { len: number; first: number; last: number; arr: number[] }
>()

/**
 * The bars' timestamps as a plain array, memoized per bar array.
 *
 * Draw paths need this every frame, and `bars.map(...)` re-allocated an
 * N-element array per draw — pure garbage during a pan, where the data has not
 * changed at all.
 */
export function timestampsOf(bars: readonly { timestamp: number }[]): number[] {
  const len = bars.length
  if (len === 0) return []
  const first = bars[0].timestamp
  const last = bars[len - 1].timestamp
  const hit = timestampsCache.get(bars)
  if (hit && hit.len === len && hit.first === first && hit.last === last) return hit.arr
  const arr = bars.map((b) => b.timestamp)
  timestampsCache.set(bars, { len, first, last, arr })
  return arr
}
